import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_6fb520ba from 'nuxt_plugin_plugin_6fb520ba' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_plugin_59626d74 from 'nuxt_plugin_plugin_59626d74' // Source: ./vuetify/plugin.js (mode: 'all')
import nuxt_plugin_markdownit_75b22ca4 from 'nuxt_plugin_markdownit_75b22ca4' // Source: ./markdownit.js (mode: 'all')
import nuxt_plugin_axios_8271da60 from 'nuxt_plugin_axios_8271da60' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_vuexpersist_13f465a2 from 'nuxt_plugin_vuexpersist_13f465a2' // Source: ../plugins/vuex-persist (mode: 'client')
import nuxt_plugin_spatzdialog_387f0bc1 from 'nuxt_plugin_spatzdialog_387f0bc1' // Source: ../plugins/spatz-dialog (mode: 'client')
import nuxt_plugin_gchart_6545aa66 from 'nuxt_plugin_gchart_6545aa66' // Source: ../plugins/gchart (mode: 'client')
import nuxt_plugin_axiosauth_274fe6d6 from 'nuxt_plugin_axiosauth_274fe6d6' // Source: ../plugins/axios-auth (mode: 'client')
import nuxt_plugin_localization_3205610f from 'nuxt_plugin_localization_3205610f' // Source: ../plugins/localization (mode: 'all')
import nuxt_plugin_routing_39669490 from 'nuxt_plugin_routing_39669490' // Source: ../plugins/routing (mode: 'all')
import nuxt_plugin_vuetify_165b1c58 from 'nuxt_plugin_vuetify_165b1c58' // Source: ../plugins/vuetify (mode: 'client')
import nuxt_plugin_vuegtag_44730e72 from 'nuxt_plugin_vuegtag_44730e72' // Source: ../plugins/vue-gtag.js (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: function() {
    const fontPreloads = [
    {
      rel: 'preload',
      as: 'font',
      type: 'font/ttf',
      crossorigin: true,
      href: '/fonts/albert-sans/AlbertSans-VariableFont_wght.ttf'
    }];

    const WEB_DOMAIN = process.env.WEB_DOMAIN || "https://moneypeak.ai/";

    const LD_JSON = [
    {
      "@context": "https://schema.org/",
      "@type": "Organization",
      "name": "MoneyPeak",
      "url": WEB_DOMAIN,
      "logo": `${WEB_DOMAIN}logo.png`,
      "sameAs": [
      "https://twitter.com/gospatzapp",
      "https://discord.gg/ayg9pQZ3eM"],

      "author": {
        "@type": "Organization",
        "name": "LCP GmbH"
      },
      "datePublished": "2021-01-01",
      "description": "Your Financial Success, Simplified"
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "applicationCategory": "FinanceApplication",
      "name": "MoneyPeak",
      "url": WEB_DOMAIN
    }];

    const currentPath = this.$route.path;
    const currentlocale = this.$getCurrentLocale();

    const alternateLinks = this.$getAvailableLocaleCodes().map((locale) => {
      const originalPath = this.$getOriginalRoutePath(currentPath, currentlocale);
      const translatedPath = this.$getTranslatedRoutePath(originalPath, locale);

      return { rel: 'alternate', href: WEB_DOMAIN + translatedPath.substring(1), hreflang: locale };
    });

    return {
      title: 'MoneyPeak - Your Financial Success, Simplified',
      htmlAttrs: {
        lang: currentlocale
      },
      meta: [
      { charset: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { hid: 'description', name: 'description', content: 'Die Finanzplattform MoneyPeak trackt und analysiert Investitionen und Portfolios. Vom Wertpapier-Depot bis zum Crypto-Kauf. Nützlich, simpel und kostenlos' },
      { hid: 'format-detection', name: 'format-detection', content: 'telephone=no' },
      { hid: 'og:type', name: 'og:type', property: 'og:type', content: 'website' },
      { hid: 'og:logo', name: 'og:logo', property: 'og:logo', content: `${WEB_DOMAIN}logo.png` },
      { hid: 'og:image', name: 'og:image', property: 'og:image', content: `${WEB_DOMAIN}spatz-desktop.png` },
      { hid: 'og:title', name: 'og:title', property: 'og:title', content: 'MoneyPeak - Your Financial Success, Simplified' },
      { hid: 'og:description', name: 'og:description', property: 'og:description', content: 'Die Finanzplattform MoneyPeak trackt und analysiert Investitionen und Portfolios. Vom Wertpapier-Depot bis zum Crypto-Kauf. Nützlich, simpel und kostenlos' },
      { hid: 'og:url', name: 'og:url', property: 'og:url', content: WEB_DOMAIN },
      { hid: 'og:site_name', name: 'og:site_name', property: 'og:site_name', content: 'MoneyPeak' },
      { hid: 'og:locale', name: 'og:locale', property: 'og:locale', content: 'de' },
      { hid: 'twitter:card', name: 'twitter:card', property: 'twitter:card', content: 'summary' },
      { hid: 'twitter:site', name: 'twitter:site', property: 'twitter:site', content: '@spatzapp' },
      { hid: 'twitter:title', name: 'twitter:title', property: 'twitter:title', content: 'Your Financial Success, Simplified' },
      { hid: 'twitter:description', name: 'twitter:description', property: 'twitter:description', content: 'Die Finanzplattform MoneyPeak trackt und analysiert Investitionen und Portfolios. Vom Wertpapier-Depot bis zum Crypto-Kauf. Nützlich, simpel und kostenlos' },
      { hid: 'twitter:image', name: 'twitter:image', property: 'twitter:image', content: `${WEB_DOMAIN}spatz-desktop.png` },
      { hid: 'Publisher', name: 'Publisher', content: 'LCP GmbH' },
      { hid: 'Copyright', name: 'Copyright', content: 'LCP GmbH' },
      { hid: 'page-topic', name: 'page-topic', content: 'Finanzen' },
      { hid: 'page-type', name: 'page-type', content: 'Kommerzielle Homepage' },
      { hid: 'audience', name: 'audience', content: 'Alle' },
      { hid: 'mobile-web-app-capable', name: 'mobile-web-app-capable', content: 'yes' },
      { hid: 'apple-mobile-web-app-status-bar-style', name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' }],

      link: [
      { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico', media: '(prefers-color-scheme: no-preference)' },
      { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico', media: '(prefers-color-scheme: light)' },
      { rel: 'icon', type: 'image/x-icon', href: '/favicon-dark.ico', media: '(prefers-color-scheme: dark)' },
      { rel: 'apple-touch-icon', type: 'image/png', href: '/apple-touch-icon.png' },
      { rel: 'dns-prefetch', href: '//moneypeak.ai' },
      { rel: 'dns-prefetch', href: '//www.googletagmanager.com' },
      { rel: 'canonical', href: WEB_DOMAIN + currentPath.substring(1) },
      ...alternateLinks,
      ...fontPreloads],

      script: [{ type: 'application/ld+json', json: LD_JSON }]
    };
  },

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_6fb520ba === 'function') {
    await nuxt_plugin_plugin_6fb520ba(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_59626d74 === 'function') {
    await nuxt_plugin_plugin_59626d74(app.context, inject)
  }

  if (typeof nuxt_plugin_markdownit_75b22ca4 === 'function') {
    await nuxt_plugin_markdownit_75b22ca4(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_8271da60 === 'function') {
    await nuxt_plugin_axios_8271da60(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuexpersist_13f465a2 === 'function') {
    await nuxt_plugin_vuexpersist_13f465a2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_spatzdialog_387f0bc1 === 'function') {
    await nuxt_plugin_spatzdialog_387f0bc1(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_gchart_6545aa66 === 'function') {
    await nuxt_plugin_gchart_6545aa66(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_axiosauth_274fe6d6 === 'function') {
    await nuxt_plugin_axiosauth_274fe6d6(app.context, inject)
  }

  if (typeof nuxt_plugin_localization_3205610f === 'function') {
    await nuxt_plugin_localization_3205610f(app.context, inject)
  }

  if (typeof nuxt_plugin_routing_39669490 === 'function') {
    await nuxt_plugin_routing_39669490(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuetify_165b1c58 === 'function') {
    await nuxt_plugin_vuetify_165b1c58(app.context, inject)
  }

  if (typeof nuxt_plugin_vuegtag_44730e72 === 'function') {
    await nuxt_plugin_vuegtag_44730e72(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
